<template>
  <div>
    <div v-if="loader" class="loader">
      <b-spinner
        variant="primary"
        type="grow"
        :label="`${this.$t('reports.loadingLabel')}...`"
      ></b-spinner>
    </div>
    <div v-else>
      <highcharts :options="chartOptions"></highcharts>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import api from '@/services/api';
import eventBus from '../../../../../eventBus';

export default {
  name: 'EvolutionPercentage',
  props: ['reportType', 'accumulated', 'dataGraph'],
  data: () => ({
    userAuth: null,
    loader: true,
    chartOptions: null,
    type: 'area',
    legend: '',
    series: [],
    categories: [],
    monthly: false,
    // reportType: 'day',
    chartPromoters: '',
    chartPassives: '',
    chartDetrators: '',
    isPanel: false,
    dataSeries: '',
  }),
  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData']),
    ...mapState('charts', ['percentageEvolution']),
    ...mapState('branches', ['myIh1', 'myIh2']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('companies', ['myMetric']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },
  watch: {
    actionGroupData: function watchActionGroupData() {
      this.prepareChart();
    },
    myActionId: function watchMyActionId() {
      this.prepareChart();
    },
    myIh1: function watchMyIh1() {
      this.prepareChart();
    },
    myIh2: function watchMyIh2() {
      this.prepareChart();
    },
    calendarDates: function watchDates() {
      this.prepareChart();
    },
    myMetric: function watchMyMetric() {
      this.changeLabel();
      this.prepareChart();
    },
    'indicatorData.value': function watchInicatorData() {
      this.prepareChart();
    },
    'indicatorData.period': async function watchIndicatorPeriod() {
      await this.prepareChart();
    },
  },
  methods: {
    ...mapActions('charts', [
      'evolutionPercentageDaily',
      'evolutionPercentagePeriod',
    ]),
    ...mapActions('calendar', ['setDefaultDates']),

    renderChartPanels(data){
      let xAxis;
      let yAxis;
      let tooltip;
      let plotOptions;

      if (this.dataGraph.accumulated) {
        this.dataSeries = [
          {
            name: `${this.chartPromoters}`,
            data: data.promoters.seriesAccumulated,
            color: '#4ce595',
          },
          {
            name: `${this.chartPassives}`,
            data: data.passive.seriesAccumulated,
            color: '#ecdb12',
          },
          {
            name: `${this.chartDetrators}`,
            data: data.detractors.seriesAccumulated,
            color: '#d62027',
          },
        ];
      } else {
       this.dataSeries = [
          {
            name: `${this.chartPromoters}`,
            data: data.promoters.series,
            color: '#4ce595',
          },
          {
            name: `${this.chartPassives}`,
            data: data.passive.series,
            color: '#ecdb12',
          },
          {
            name: `${this.chartDetrators}`,
            data: data.detractors.series,
            color: '#d62027',
          },
        ];
      }

      this.categories = data.categories;

      let typeGraph = '';

      if(!this.dataGraph.graph || this.dataGraph.graph === 'default'){
        typeGraph = 'area';
      }else{
        typeGraph = this.dataGraph.graph;
      }

      if (typeGraph === 'area') {
        xAxis = {
          categories: data.categories,
          tickmarkPlacement: 'on',
          title: {
            enabled: false,
          },
        };

        yAxis = {
          gridLineWidth: 0,
          labels: {
            enabled: false,
            format: '{value}%',
          },
          title: {
            enabled: false,
          },
        };

        tooltip = {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>: <b>{point.percentage:.1f}%</b> ({point.y:,.0f})<br/>',
          split: true,
        };

        plotOptions = {
          area: {
            stacking: 'percent',
            lineColor: '#ffffff',
            lineWidth: 1,
            dataLabels: {
              enabled: true,
            },
            marker: {
              lineWidth: 1,
              lineColor: '#ffffff',
            },
            accessibility: {
              pointDescriptionFormatter: (point) => {
                function round(x) {
                  return Math.round(x * 100) / 100;
                }
                return `${point.index + 1}, ${point.category}, ${
                  point.y
                } millions, ${round(point.percentage)}%, ${point.series.name}`;
              },
            },
          },
        };
      } else if (typeGraph === 'column' || typeGraph === 'line') {
        xAxis = {
          categories: data.categories,
        };

        yAxis = {
          min: 0,
          title: {
            text: '',
          },
        };

        tooltip = {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.percentage:.0f}%)<br/>',
          shared: true,
        };

        plotOptions = {
          column: {
            stacking: 'percent',
            dataLabels: {
              enabled: true,
            },
          },
          line: {
            dataLabels: {
              enabled: true,
            },
          },
        };
      }

      this.chartOptions = {
        chart: {
          type: typeGraph,
          height: this.isPanel ? this.dataGraph.layout.h * 36 : '',
          borderColor: 'rgba(0, 0, 0, 0.09)',
          style: {
            fontFamily:
              '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
            fontSize: '12px',
            width: '100%',
          },
        },
        title: {
          align: 'left',
          text: this.isPanel
            ? ''
            : `${this.$t('reports.percentageEvolution.reportTitle')} ${
                this.legend
              }`,
          style: {
            color: '#5f6368',
          },
        },
        xAxis,
        yAxis,
        tooltip,
        plotOptions,
        legend: {
          enabled: false,
        },
        series: this.dataSeries,
       };
    },

    renderChart() {
      let xAxis;
      let yAxis;
      let tooltip;
      let plotOptions;

      if (this.accumulated) {
        this.series = [
          {
            name: `${this.chartPromoters}`,
            data: this.percentageEvolution.promoters.seriesAccumulated,
            color: '#4ce595',
          },
          {
            name: `${this.chartPassives}`,
            data: this.percentageEvolution.passive.seriesAccumulated,
            color: '#ecdb12',
          },
          {
            name: `${this.chartDetrators}`,
            data: this.percentageEvolution.detractors.seriesAccumulated,
            color: '#d62027',
          },
        ];
      } else {
        this.series = [
          {
            name: `${this.chartPromoters}`,
            data: this.percentageEvolution.promoters.series,
            color: '#4ce595',
          },
          {
            name: `${this.chartPassives}`,
            data: this.percentageEvolution.passive.series,
            color: '#ecdb12',
          },
          {
            name: `${this.chartDetrators}`,
            data: this.percentageEvolution.detractors.series,
            color: '#d62027',
          },
        ];
      }

      this.categories = this.percentageEvolution.categories;

      if (this.type === 'area') {
        xAxis = {
          categories: this.categories,
          tickmarkPlacement: 'on',
          title: {
            enabled: false,
          },
        };

        yAxis = {
          gridLineWidth: 0,
          labels: {
            enabled: false,
            format: '{value}%',
          },
          title: {
            enabled: false,
          },
        };

        tooltip = {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>: <b>{point.percentage:.1f}%</b> ({point.y:,.0f})<br/>',
          split: true,
        };

        plotOptions = {
          area: {
            stacking: 'percent',
            lineColor: '#ffffff',
            lineWidth: 1,
            dataLabels: {
              enabled: true,
            },
            marker: {
              lineWidth: 1,
              lineColor: '#ffffff',
            },
            accessibility: {
              pointDescriptionFormatter: (point) => {
                function round(x) {
                  return Math.round(x * 100) / 100;
                }
                return `${point.index + 1}, ${point.category}, ${
                  point.y
                } millions, ${round(point.percentage)}%, ${point.series.name}`;
              },
            },
          },
        };
      } else if (this.type === 'column' || this.type === 'line') {
        xAxis = {
          categories: this.categories,
        };

        yAxis = {
          min: 0,
          title: {
            text: '',
          },
        };

        tooltip = {
          pointFormat:
            '<span style="color:{series.color}">{series.name}</span>: <b>{point.y}</b> ({point.percentage:.0f}%)<br/>',
          shared: true,
        };

        plotOptions = {
          column: {
            stacking: 'percent',
            dataLabels: {
              enabled: true,
            },
          },
          line: {
            dataLabels: {
              enabled: true,
            },
          },
        };
      }

      this.chartOptions = {
        chart: {
          type: this.type,
          height: this.isPanel ? this.dataGraph.layout.h * 36 : '',
          borderColor: 'rgba(0, 0, 0, 0.09)',
          style: {
            fontFamily:
              '-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,"Helvetica Neue",Arial,"Noto Sans",sans-serif,"Apple Color Emoji","Segoe UI Emoji","Segoe UI Symbol","Noto Color Emoji"',
            fontSize: '12px',
            width: '100%',
          },
        },
        title: {
          align: 'left',
          text: this.isPanel
            ? ''
            : `${this.$t('reports.percentageEvolution.reportTitle')} ${
                this.legend
              }`,
          style: {
            color: '#5f6368',
          },
        },
        xAxis,
        yAxis,
        tooltip,
        plotOptions,
        legend: {
          enabled: false,
        },
        series: this.series,
      };
    },

    setChart() {
      this.loader = true;

      let ih1Parsed =  ih1 && ih1.length > 0 ? ih1.map((item) => item._id): null

      this.evolutionPercentageDaily({
        companyId: this.userAuth.companyId,
        actionId: this.myActionId !== '0' ? this.myActionId : null,
        //token: this.userAuth.token,
        ih1: ih1Parsed ? ih1Parsed : null,
        ih2: this.myIh2 === '0' ? null : this.myIh2,
        startDate: this.calendarDates.start ? this.calendarDates.start : null,
        endDate: this.calendarDates.end ? this.calendarDates.end : null,
        metric: this.myMetric ? this.myMetric : null,
        groupId: this.actionGroupData._id,
        indicator: this.indicatorName,
        indicatorValue: this.indicatorData.value,
        indicatorRange: this.indicatorData.range,
        _id: this.dataGraph._id,
      }).then(() => {
        this.loader = false;
        this.renderChart();
      });
    },
    setChartPeriod() {

      let ih1Parsed = ih1 && ih1.length > 0 ? ih1.map((item) => item._id): null
      this.loader = true;
      this.evolutionPercentagePeriod({
        companyId: this.userAuth.companyId,
        actionId: this.myActionId !== '0' ? this.myActionId : null,
        //token: this.userAuth.token,
        ih1: ih1Parsed ? ih1Parsed : null,
        ih2: this.myIh2 === '0' ? null : this.myIh2,
        startDate: this.calendarDates.start ? this.calendarDates.start : null,
        endDate: this.calendarDates.end ? this.calendarDates.end : null,
        metric: this.myMetric ? this.myMetric : null,
        groupId: this.actionGroupData._id,
        indicator: this.indicatorName,
        indicatorValue: this.indicatorData.value,
        indicatorRange: this.indicatorData.range,
        reportType: this.reportType,
        _id: this.dataGraph._id,
      }).then(() => {
        this.loader = false;
        this.renderChart();
      });
    },
    changeLabel() {
      if (this.myMetric.indexOf('nps') !== -1) {
        this.chartPromoters = this.$t('reports.promoterLabel');
        this.chartPassives = this.$t('reports.neutralLabel');
        this.chartDetrators = this.$t('reports.detractorLabel');
      } else if (this.myMetric.indexOf('ces') !== -1) {
        this.chartPromoters = '4-5';
        this.chartPassives = '3';
        this.chartDetrators = '1-2';
      }
    },

    changeLabelPanel() {
      if (this.dataGraph.metric.indexOf('nps') !== -1) {
        this.chartPromoters = this.$t('reports.promoterLabel');
        this.chartPassives = this.$t('reports.neutralLabel');
        this.chartDetrators = this.$t('reports.detractorLabel');
      } else if (this.dataGraph.metric.indexOf('ces') !== -1) {
        this.chartPromoters = '4-5';
        this.chartPassives = '3';
        this.chartDetrators = '1-2';
      }
    },

    prepareChart() {
      switch (this.reportType) {
        case 'day':
          if (this.accumulated) {
            this.legend = `- ${this.$t('reports.accDailyLabel')}`;
          } else {
            this.legend = `- ${this.$t('reports.dailyLabel')}`;
          }
          this.setChart();
          break;
        case 'week':
          if (this.accumulated) {
            this.legend = `- ${this.$t('reports.accWeeklyLabel')}`;
          } else {
            this.legend = `- ${this.$t('reports.weeklyLabel')}`;
          }
          this.setChartPeriod();
          break;
        case 'month':
          if (this.accumulated) {
            this.legend = `- ${this.$t('reports.accMonthlyLabel')}`;
          } else {
            this.legend = `- ${this.$t('reports.monthlyLabel')}`;
          }
          this.setChartPeriod();
          break;
        case 'quarter':
          if (this.accumulated) {
            this.legend = `- ${this.$t('reports.accQuarterlyLabel')}`;
          } else {
            this.legend = `- ${this.$t('reports.quarterlyLabel')}`;
          }
          this.setChartPeriod();
          break;
        default:
          break;
      }
    },

    async setColumnCard(data) {
      this.loader = true;

      let ih1Parsed = data.ih1 && data.ih1.length > 0 ? data.ih1.map((item) => item._id): null
      
      const response = await api.get(
        `/reports/percentil-evolution/period-panel`,
        {
          headers: { 'x-access-token': this.userAuth.token },
          params: {
            companyId: this.userAuth.companyId,
            actionId: data.actionId ? data.actionId._id : null,
            //token: this.userAuth.token,
            ih1: ih1Parsed ? ih1Parsed : null,
            reportType: this.reportType ? this.reportType : 'area',
            period: data.period ? data.period : null,
            metric: data.metric ? data.metric : null,
            indicatorValue: data.valueIndicator ? data.valueIndicator : null,
            indicator: data.indicator ? data.indicator : null,
            groupId: data.actionGroupId ? data.actionGroupId._id : null,
            _id: this.dataGraph._id,
          },
        },
      );

      

        if (this.dataGraph.accumulated) {
            this.renderChartPanels(
              response.data,
            );
          } else {
            this.renderChartPanels(
              response.data
            );
          }

        this.loader = false;
    },
  },
  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));
  },
  async mounted() {
    if (this.$route.fullPath.indexOf('panels') !== -1) {
      this.isPanel = true;
      this.setColumnCard(this.dataGraph);
      this.type = 'area';
      this.changeLabelPanel();
    } else {
      this.changeLabel();
      this.prepareChart();
      eventBus.$on('accumulated-report', (checked) => {
        this.accumulated = checked;
        this.prepareChart();
      });
      eventBus.$on('data-source', (checked) => {
        this.reportType = checked;
        this.monthly = checked === 'month';
        this.prepareChart();
      });
      eventBus.$on('switch-chart', (chart) => {
        this.type = chart;
        this.prepareChart();
      });
    }
  },
  beforeDestroy() {
    // eventBus.$off('change-dates');
    eventBus.$off('switch-chart');
    eventBus.$off('data-source');
    eventBus.$off('accumulated-report');
  },
};
</script>

<style lang="scss" scoped>
.loader {
  width: 100%;
  height: 320px;
  background-color: #fff;
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/molecules/highcharts/reports/panels/evolution-percentage-panels.vue